<template>
  <article class="flex flex-col gap-4 rounded-3xl border border-neutral-300 bg-white p-4 lg:grid lg:grid-cols-2 lg:gap-10 lg:p-8">
    <div class="contents lg:flex lg:flex-col lg:gap-4">
      <ResponsiveImage
        v-if="pack.featuredImage"
        :image="pack.featuredImage"
        :alt="pack.title"
        :width="448"
        class="order-1 aspect-[2/1] w-full object-contain"
      />
      <details
        v-if="contents.length"
        class="group order-3 border-y border-neutral-200"
      >
        <summary
          class="flex cursor-pointer select-none list-none items-center justify-between py-3 font-semibold [&::-webkit-details-marker]:hidden"
          data-gtm-action="variety packs page flavors expand"
        >
          What's in this pack
          <svg-icon name="plus" class="w-4 h-4 shrink-0 group-open:hidden" />
          <svg-icon name="minus" class="w-4 h-4 shrink-0 hidden group-open:block" />
        </summary>
        <ul class="flex flex-col gap-2 pb-3">
          <li
            v-for="entry in contents"
            :key="entry.product.key"
          >
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg pr-2 text-left hover:bg-neutral-100"
              data-gtm-action="variety packs page flavor detail click"
              :data-gtm-label="entry.product.title"
              @click="openProduct(entry.product.bpId)"
            >
              <span class="size-12 shrink-0">
                <ResponsiveImage
                  v-if="entry.product.profileImage"
                  :image="entry.product.profileImage"
                  :width="48"
                  alt=""
                  class="size-full object-contain"
                />
              </span>
              <span class="grow">{{ entry.quantity }} &times; {{ entry.product.title }}</span>
              <svg-icon
                name="info"
                class="w-4 h-4 shrink-0 text-blue-500"
              />
            </button>
          </li>
        </ul>
      </details>
    </div>

    <div class="contents lg:flex lg:flex-col lg:gap-4">
      <div class="order-2 flex flex-col items-center gap-2 text-center lg:items-start lg:text-left">
        <h3 class="text-2xl font-semibold">
          {{ pack.title }}
        </h3>
        <DynamicStars
          v-if="pack.ratingCount"
          :count="pack.ratingCount"
          :rating="pack.ratingAverage || 5"
        />
        <div
          v-if="pack.descriptionHtml"
          class="text-base leading-snug text-neutral-700"
          v-html="pack.descriptionHtml"
        />
      </div>

      <div class="order-4 flex flex-col gap-4">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
          <div class="flex flex-col gap-1 text-sm font-semibold">
            Pack Size
            <Listbox v-model="size">
              <div class="relative z-20 w-full font-normal">
                <ListboxButton class="hui-dropdown-button w-full">
                  <span class="block truncate">
                    {{ size }} Pouches &middot; {{ formatCents(priceFor(size, plan)) }}
                  </span>
                  <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                    <svg-icon
                      name="chevron-down"
                      class="w-3 h-3 text-gray-400"
                    />
                  </span>
                </ListboxButton>
                <ListboxOptions class="hui-dropdown-list left-0 w-full">
                  <ListboxOption
                    v-for="s in sizes"
                    :key="s"
                    v-slot="{ active }"
                    :value="s"
                    as="template"
                  >
                    <li :class="active && 'active'">
                      <span class="whitespace-nowrap">
                        {{ s }} Pouches &middot; {{ formatCents(priceFor(s, plan)) }}
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </div>
            </Listbox>
          </div>
          <div class="flex flex-col gap-1 text-sm font-semibold">
            Plan Type
            <Listbox v-model="plan">
              <div class="relative z-10 w-full font-normal">
                <ListboxButton class="hui-dropdown-button w-full">
                  <span class="block truncate">
                    {{ planLabel(plan) }} &middot; {{ formatCents(priceFor(size, plan)) }}
                  </span>
                  <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                    <svg-icon
                      name="chevron-down"
                      class="w-3 h-3 text-gray-400"
                    />
                  </span>
                </ListboxButton>
                <ListboxOptions class="hui-dropdown-list left-0 w-full">
                  <ListboxOption
                    v-for="option in planOptions"
                    :key="option"
                    v-slot="{ active }"
                    :value="option"
                    as="template"
                  >
                    <li :class="active && 'active'">
                      <span class="whitespace-nowrap">
                        {{ planLabel(option) }} &middot; {{ formatCents(priceFor(size, option)) }}
                      </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </div>
            </Listbox>
          </div>
        </div>

        <div
          v-if="pricing"
          class="flex flex-col gap-3 rounded-xl p-3"
          :class="isSub ? 'bg-green-50' : 'border border-green-500 bg-white'"
        >
          <p class="font-bold">
            <template v-if="isSub">
              {{ pricing.firstOrderDiscountPercent ? `Save ${pricing.firstOrderDiscountPercent}% on your first order` : 'Subscribe & Save' }}
            </template>
            <template v-else>
              {{ pricing.firstOrderDiscountPercent ? `Wait! Don't You Want ${pricing.firstOrderDiscountPercent}% Off?` : 'Last chance to save!' }}
            </template>
          </p>
          <div class="plan-benefits flex flex-wrap justify-between gap-2 text-sm">
            <div class="flex flex-col gap-1">
              <div
                v-if="pricing.firstSavings > 0"
                class="flex items-center gap-1"
              >
                <svg-icon
                  name="callout-check"
                  class="w-3 h-3"
                  :class="checkColor"
                />
                {{ $t('/bundler.savings_off_first_order', { firstSavings: formatCents(pricing.firstSavings) }) }}
              </div>
              <div
                v-if="pricing.savings > 0"
                class="flex items-center gap-1"
              >
                <svg-icon
                  name="callout-check"
                  class="w-3 h-3"
                  :class="checkColor"
                />
                {{ $t('/bundler.savings_off_future_orders', { savings: formatCents(pricing.savings) }) }}
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-1">
                <svg-icon
                  name="callout-check"
                  class="w-3 h-3"
                  :class="checkColor"
                />
                <span v-html="$t('/bundler.5_day_email_reminder_cancel_anytime')" />
              </div>
              <div class="flex items-center gap-1">
                <svg-icon
                  name="callout-check"
                  class="w-3 h-3"
                  :class="checkColor"
                />
                {{ $t('/bundler.free_new_flavor_with_every_box') }}
              </div>
            </div>
          </div>
          <button
            v-if="!isSub"
            class="button +green"
            data-gtm-action="variety packs page switch to subscription click"
            @click="plan = 'subscription'"
          >
            Switch to Subscribe &amp; Save
          </button>
        </div>

        <DisclosureButton
          class="+green +large w-full"
          :disabled="!ready || checkout.loading"
          :value="priceFor(size, plan)"
          data-gtm-action="variety packs page checkout click"
          :data-gtm-label="pack.title"
          @click="startCheckout"
        >
          Checkout
        </DisclosureButton>

        <a
          :href="customizeUrl"
          class="button +border +large"
          data-gtm-action="variety packs page customize click"
          :data-gtm-label="pack.title"
        >
          Customize This Bundle
        </a>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { formatCents, sum } from '@/utils'
import { useBundleStore, useVarietyPacksStore, useBundleCheckoutStore, useThemeStore } from '@/stores'
import { useBreakpoints } from '@/plugins/global.01-breakpoints'
import { useAmplitude } from '@/services/amplitude'
import ProductModalWrapper from '@/components/products/product-modal-wrapper.vue'
import ResponsiveImage from '@/components/shared/v2/responsive-image.vue'
import DisclosureButton from '@/components/bundle/disclosure-button.vue'
import { DynamicStars, SvgIcon } from '@oatsovernight/storybook'
import type { BundlePlan, BundleSize } from '@/types/bundle'
import type { GraphqlVarietyPack } from '@/types/shopify'

export interface Props {
  pack: GraphqlVarietyPack
}
const props = defineProps<Props>()

const bundle = useBundleStore()
const packsStore = useVarietyPacksStore()
const checkout = useBundleCheckoutStore()
const { trackBundle } = useAmplitude()

const sizes = computed(() => bundle.bundleSizes.filter(s => props.pack.skuMap?.[String(s)]))
const chosenSize = ref<BundleSize>()
const size = computed<BundleSize>({
  get: () => chosenSize.value ?? sizes.value[0],
  set: (value) => chosenSize.value = value
})
const plan = ref<BundlePlan>('subscription')
const planOptions: BundlePlan[] = [ 'subscription', 'onetime' ]
const planLabel = (value: BundlePlan) => value === 'subscription' ? 'Subscribe & Save' : 'One-Time Purchase'
const isSub = computed(() => plan.value === 'subscription')
const checkColor = computed(() => isSub.value ? 'text-green-400' : 'text-gray-300')

const contents = computed(() => packsStore.contents(props.pack, size.value))
const pricing = computed(() => bundle.bundlePricing[size.value])

const priceFor = (forSize: BundleSize, forPlan: BundlePlan) => {
  const p = bundle.bundlePricing[forSize]
  return (forPlan === 'subscription' ? p?.firstOrderCents : p?.onetimeReferralCents) ?? 0
}

const ready = computed(() => !!pricing.value && sum(...contents.value.map(c => c.quantity)) === size.value)

const customizeUrl = computed(() => `/products/custom-bundle?curated=${props.pack.handle}&confirmation=modal&type=${plan.value}&size=${size.value}`)

const fillBundle = (track = false) => {
  bundle.setSelectionFromContents(contents.value)
  bundle.plan = plan.value
  bundle.checkoutSwitchToSubscription = false
  if (track) {
    void trackBundle(undefined, props.pack.title)
  }
}

const { sm } = useBreakpoints()
const { drawer, modal } = useThemeStore()
const openProduct = (bundleProductId: number) => {
  const shell = sm.value ? drawer : modal
  shell.open(ProductModalWrapper, { bundleProductId })
}

const startCheckout = async () => {
  fillBundle()
  await nextTick()
  await checkout.addToCart()
}
</script>
